import { useState } from 'react';
import type { CSSProperties } from 'react';

/*
 * Leaving the till.
 *
 * The window covers the whole screen with no title bar, no border and no
 * taskbar, so there is no Windows affordance to fall back on: if a screen does
 * not offer a way out, there is none. «خروج از برنامه» lived only in the till's
 * menu, which exists only once a shift is open — so closing the shift left a
 * cashier inside a fullscreen app with no exit, and so did a boot that could not
 * reach the agent. Our user found both.
 *
 * Shared from here rather than copied, because the rule is one rule: every
 * screen that can BE the whole app offers the exit.
 */

/** True when the bundle is running inside the Windows till, not a browser. */
export function hostedInTill(): boolean {
  return typeof window !== 'undefined'
    && typeof (window as { chrome?: { webview?: { postMessage?: unknown } } }).chrome?.webview?.postMessage === 'function';
}

/**
 * Asks the host window to close.
 *
 * The till covers the whole screen with no title bar and no taskbar, so this
 * message is the only way back to the desktop. The wrapper checks the origin
 * before acting on it.
 */
export function exitTill() {
  (window as unknown as { chrome: { webview: { postMessage: (m: string) => void } } })
    .chrome.webview.postMessage('luma.exit');
}

/**
 * Asks the host window to get out of the way.
 *
 * Not the same request as exitTill, and the difference matters at a counter:
 * a cashier who needs the printer queue or the bank's site should not have to
 * close the till to see them. The window has no title bar, so there is no
 * minimise button to reach for — this message is the only one.
 *
 * No confirmation: nothing is lost, and the till is one click away again on
 * the taskbar.
 */
export function minimiseTill() {
  (window as unknown as { chrome: { webview: { postMessage: (m: string) => void } } })
    .chrome.webview.postMessage('luma.minimise');
}

export function ConfirmExit({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(32,30,29,.45)' }}
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed', zIndex: 41, insetInline: 0, top: '28%', margin: '0 auto',
          width: 'min(420px, 92vw)', background: 'var(--luma-card)',
          border: '1px solid var(--luma-line)', borderRadius: 16, padding: 20,
          boxShadow: '0 24px 64px rgba(0,0,0,.28)',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>خروج از برنامه؟</div>
        <div style={{ fontSize: 14, color: 'var(--luma-ink-2)', lineHeight: 1.8, marginBottom: 16 }}>
          صندوق بسته می‌شود و به میزکار برمی‌گردید. فروش ثبت‌شده از بین نمی‌رود.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, height: 48, borderRadius: 12, fontWeight: 700, fontSize: 15,
              border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
            }}
          >
            انصراف
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1, height: 48, borderRadius: 12, fontWeight: 700, fontSize: 15,
              border: 'none', background: 'var(--luma-danger, #A8352C)', color: '#fff',
            }}
          >
            خروج
          </button>
        </div>
      </div>
    </>
  );
}

/*
 * A quiet exit for the screens that have no menu to put it in.
 *
 * Deliberately small and in the corner: it is a way out, not an action anybody
 * should be invited to take mid-service. Rendered only inside the Windows till —
 * in a browser the tab already closes, and a button that cannot work is worse
 * than no button.
 */
export function ExitCorner() {
  const [confirm, setConfirm] = useState(false);
  if (!hostedInTill()) return null;

  const corner: CSSProperties = {
    height: 38, padding: '0 14px', borderRadius: 10, cursor: 'pointer',
    border: '1px solid var(--luma-line)', background: 'var(--luma-card)',
    color: 'var(--luma-ink-2)', fontSize: 13, fontWeight: 700,
  };

  return (
    <>
      <div
        style={{
          position: 'fixed', insetInlineStart: 16, bottom: 16, zIndex: 30,
          display: 'flex', gap: 8,
        }}
      >
        {/*
          Minimise first, and without a confirmation: it is the one a cashier
          actually wants most of the time, and nothing is lost by it. Leaving
          only «خروج از برنامه» meant closing the till to read an email.
        */}
        <button onClick={minimiseTill} style={corner}>
          کوچک کردن
        </button>
        <button onClick={() => setConfirm(true)} style={corner}>
          خروج از برنامه
        </button>
      </div>
      {confirm && <ConfirmExit onCancel={() => setConfirm(false)} onConfirm={exitTill} />}
    </>
  );
}
